import { useEffect, useRef, useState } from 'react';
import { Compartment, EditorState } from '@codemirror/state';
import { EditorView } from '@codemirror/view';
import { LanguageDescription } from '@codemirror/language';
import { languages } from '@codemirror/language-data';
import { MergeView as CodeMirrorComparisonView, unifiedMergeView } from '@codemirror/merge';
import { cn } from '../../lib/utils';
import { Button } from '../button';
import { comparisonBaseExtensions } from './editor-theme';

/**
 * Read-only comparison of one file at two stored versions
 * (app-shell ADR-018).
 *
 * A SIBLING of {@link MergeView}, never an extension of it: that surface owns an
 * editable buffer with resolution actions and a save, none of which a historical
 * read may carry. What the two share is the editor extension and the lazy host —
 * both sides here are `readOnly` + non-editable, there are no per-change
 * transfer controls, and nothing this component renders can write.
 */
export interface VersionComparisonViewProps {
  /** Workspace-relative path — drives language selection and the header. */
  filename: string;
  /** The earlier document. */
  before: string;
  /** The later document. */
  after: string;
  /** Short label for the earlier side (e.g. "before this task"). */
  beforeLabel?: string;
  /** Short label for the later side. */
  afterLabel?: string;
  onClose?: () => void;
  className?: string;
}

// Below this width the two-column layout is unreadable — collapse to the
// single-document presentation, which carries the same change decorations.
const NARROW_PX = 680;

export function VersionComparisonView({
  filename,
  before,
  after,
  beforeLabel = 'earlier',
  afterLabel = 'current',
  onClose,
  className,
}: VersionComparisonViewProps) {
  const hostRef = useRef<HTMLDivElement | null>(null);
  const viewRef = useRef<CodeMirrorComparisonView | EditorView | null>(null);
  const languageCompartment = useRef(new Compartment());
  const [langError, setLangError] = useState<string | null>(null);

  // null until the first measure — building at the wrong width and instantly
  // rebuilding would drop CM6 state for nothing.
  const [narrow, setNarrow] = useState<boolean | null>(null);
  useEffect(() => {
    const el = hostRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (typeof w === 'number' && w > 0) setNarrow(w < NARROW_PX);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host || narrow === null) return;

    const readOnly = [EditorState.readOnly.of(true), EditorView.editable.of(false)];

    if (narrow) {
      viewRef.current = new EditorView({
        parent: host,
        state: EditorState.create({
          doc: after,
          extensions: [
            ...comparisonBaseExtensions(),
            languageCompartment.current.of([]),
            ...readOnly,
            unifiedMergeView({ original: before, mergeControls: false }),
          ],
        }),
      });
    } else {
      viewRef.current = new CodeMirrorComparisonView({
        parent: host,
        a: {
          doc: before,
          extensions: [
            ...comparisonBaseExtensions(),
            languageCompartment.current.of([]),
            ...readOnly,
          ],
        },
        b: {
          doc: after,
          extensions: [...comparisonBaseExtensions(), ...readOnly],
        },
        highlightChanges: true,
        gutter: true,
      });
    }

    return () => {
      viewRef.current?.destroy();
      viewRef.current = null;
    };
  }, [narrow, before, after]);

  // Same language registry as the other editor surfaces, injected into
  // whichever presentation is live.
  useEffect(() => {
    setLangError(null);
    const desc = LanguageDescription.matchFilename(languages, filename.split('/').pop() ?? filename);
    if (!desc) return; // plain text — correct for unknown extensions
    let cancelled = false;
    desc
      .load()
      .then((support) => {
        if (cancelled) return;
        const v = viewRef.current;
        const effects = languageCompartment.current.reconfigure(support);
        if (v instanceof EditorView) v.dispatch({ effects });
        else if (v instanceof CodeMirrorComparisonView) v.a.dispatch({ effects });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setLangError(err instanceof Error ? err.message : String(err));
      });
    return () => {
      cancelled = true;
    };
    // narrow: re-apply after a rebuild swaps the underlying view.
  }, [filename, narrow]);

  return (
    <div
      className={cn('flex h-full min-h-0 flex-col rounded-md bg-muted/30 text-body', className)}
      data-testid="version-comparison-view"
      data-filename={filename}
    >
      <div className="flex flex-wrap items-center gap-2 border-b px-3 py-1.5">
        <span className="min-w-0 flex-1 truncate font-mono text-caption text-muted-foreground">
          {filename}
          <span className="ml-2">
            · {beforeLabel} vs. {afterLabel} · read only
          </span>
          {langError && (
            <span className="ml-2 text-destructive" role="alert">
              · syntax highlighting failed to load: {langError}
            </span>
          )}
        </span>
        {onClose && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="h-6 px-2 text-caption"
            data-testid="version-comparison-close"
            onClick={onClose}
          >
            Close
          </Button>
        )}
      </div>
      <div
        ref={hostRef}
        className="min-h-0 flex-1 overflow-auto"
        data-testid="version-comparison-body"
      />
    </div>
  );
}
